import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import fleetAPI from '../service/fleetAPI'
import { CreateFleetPayload, UpdateFleetPayload } from '../types/fleet.types'
import { DimmingProfile } from '../../dimmingProfile'

export type CallbackFunction = (result: { result?: any; error?: any }) => void

// List all fleets for the account (server scopes by JWT account).
export const useFleets = () => {
  const fleetsQuery = useQuery({
    queryKey: ['fleets'],
    queryFn: () => fleetAPI.getAllFleets(0, 0),
    staleTime: 30_000,
  })
  return { fleetsQuery }
}

export const useFleet = (fleetId?: string) => {
  return useQuery({
    queryKey: ['fleet', fleetId],
    queryFn: () => fleetAPI.getFleetById(fleetId as string),
    enabled: Boolean(fleetId),
  })
}

// Set of dev_euis already assigned to ANY fleet — used to enforce the
// 1-fleet-membership rule in the luminaire picker (client-side filter; the
// server also rejects duplicates, plan §4 F2).
export const useAssignedDevEuis = (excludeFleetId?: string) => {
  const { fleetsQuery } = useFleets()
  return useMemo(() => {
    const set = new Set<string>()
    for (const f of fleetsQuery.data?.data ?? []) {
      if (excludeFleetId && f.fleet_id === excludeFleetId) continue
      for (const eui of f.member_dev_euis ?? []) set.add(eui)
    }
    return set
  }, [fleetsQuery.data, excludeFleetId])
}

export const useCreateFleet = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      payload,
      callbackFunc,
    }: {
      payload: CreateFleetPayload
      callbackFunc?: CallbackFunction
    }) =>
      fleetAPI
        .createFleet(payload)
        .then((result) => {
          queryClient.invalidateQueries('fleets')
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        }),
    onSettled: () => {
      queryClient.invalidateQueries('fleets')
    },
  })
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error as AxiosError,
  }
}

export const useUpdateFleet = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      fleetId,
      payload,
      callbackFunc,
    }: {
      fleetId: string
      payload: UpdateFleetPayload
      callbackFunc?: CallbackFunction
    }) =>
      fleetAPI
        .updateFleet(fleetId, payload)
        .then((result) => {
          queryClient.invalidateQueries('fleets')
          queryClient.invalidateQueries(['fleet', fleetId])
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        }),
  })
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error as AxiosError,
  }
}

export const useDeleteFleet = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      fleetId,
      callbackFunc,
    }: {
      fleetId: string
      callbackFunc?: CallbackFunction
    }) =>
      fleetAPI
        .deleteFleet(fleetId)
        .then((result) => {
          queryClient.invalidateQueries('fleets')
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        }),
  })
  return {
    mutate: mutation.mutate,
    isLoading: mutation.isLoading,
    error: mutation.error as AxiosError,
  }
}

// ── Profile (the fleet's dimming profile) ───────────────────────────────────
// Wire contract §9: the profile document is keyed by `fleet_id` (1:1). The
// numeric `profile_id` is only the 0x21 wire slot; 255 = "no_profile" means the
// fleet has no profile yet — skip the fetch entirely in that case. Any other
// value (including 0 for a freshly-created fleet) means the slot exists but
// the document may not have been written yet → fetch and tolerate a 404 (the
// API returns null, React Query stores null, the editor starts empty).
export const useFleetProfile = (fleetId?: string, profileId?: number) => {
  return useQuery<DimmingProfile | null>({
    queryKey: ['dimmingProfile', fleetId],
    enabled: Boolean(fleetId) && profileId !== 255,
    retry: false,
    queryFn: () => fleetAPI.getProfileByFleetId(fleetId as string),
  })
}

export const useSaveProfile = () => {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({
      profile,
      callbackFunc,
    }: {
      profile: DimmingProfile
      callbackFunc?: CallbackFunction
    }) => {
      // Profiles are addressed by fleet_id (§9). version 0 (new) -> POST (the
      // server allocates profile_id); otherwise PUT. The backend owns
      // version++/CAS.
      const op =
        profile.version > 0
          ? fleetAPI.updateProfile(profile.fleet_id, profile)
          : fleetAPI.createProfile(profile.fleet_id, profile)
      return op
        .then((result) => {
          queryClient.invalidateQueries(['dimmingProfile', profile.fleet_id])
          queryClient.invalidateQueries('fleets')
          callbackFunc?.({ result, error: undefined })
          return result
        })
        .catch((error) => {
          callbackFunc?.({ result: undefined, error })
          return Promise.reject(error)
        })
    },
  })
  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
    error: mutation.error as AxiosError,
  }
}
